import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import Dropdown, { DropdownItem } from '../common/Dropdown/Dropdown';
import Icon from '../common/Icon';

import { ReactComponent as CaretIcon } from '../../icons/arrow-down.svg';

import { isSameKey, spellKey } from '../../utils/partKey';

import './PartKeyPicker.scss';

/**
 * The key a part declares, out of the twenty-four the song can move to.
 *
 * A menu rather than a <select>, and the two modes side by side rather than one under the other: a
 * native select stacks its optgroups, so reaching a minor key means scrolling past twelve major
 * ones on the phone this is mostly used on. In two columns the whole list is on screen at once and
 * any key is one tap away.
 *
 * Portalled for the same reason the "Aa" menu beside it is — the row it opens from scrolls
 * sideways, and a menu anchored inside it would be clipped.
 *
 * The chosen key is marked by washing its row in the accent rather than by a tick, which in a grid
 * this dense would cost every cell the width of one. The key the part is in without one of its
 * own is ringed, so "Same key" still shows where in the grid the part sits.
 */
const PartKeyPicker = ({ options, value, inForceKey, disabled = false, title, onChange }) => {
  const { t } = useTranslation();
  const { songOption, groups } = options;

  // Brings the marked key into view on open, since twelve rows a column overflow the menu
  const handleOpenChange = (open) => {
    if (!open) return;

    requestAnimationFrame(() => {
      document
        .querySelector('.PartKeyMenu .key-columns .key-option.on, .PartKeyMenu .key-columns .key-option.current')
        ?.scrollIntoView({ block: 'nearest' });
    });
  };

  return (
    <Dropdown
      portal
      align="left"
      className="PartKeyPicker"
      triggerClassName="key-trigger"
      menuClassName="PartKeyMenu"
      title={title}
      ariaLabel={t('songForm.lyricsActions.partKeyLabel')}
      disabled={disabled}
      onOpenChange={handleOpenChange}
      trigger={
        <>
          {/* The part's own spelling, not the option's: a key rewritten by a transpose still names
              itself even when it no longer matches an entry in the list */}
          <span className="value">{value ? spellKey(value) : songOption.label}</span>
          <Icon icon={<CaretIcon aria-hidden="true" />} className="caret" />
        </>
      }
    >
      <DropdownItem
        className={cx('key-option', 'song-option', { on: !value })}
        subtitle={songOption.hint}
        aria-pressed={!value}
        onClick={() => onChange('')}
      >
        {songOption.label}
      </DropdownItem>
      <div className="key-columns">
        {groups.map((group) => (
          <section key={group.label} className="key-group">
            <h4 className="group-title">{group.label}</h4>
            {group.options.map((option) => {
              // Matched by key rather than by string, so a spelling the song was transposed into
              // still lands on the entry it names
              const chosen = isSameKey(option.value, value);
              const current = !value && isSameKey(option.value, inForceKey);

              return (
                <DropdownItem
                  key={option.value}
                  className={cx('key-option', { on: chosen, current })}
                  aria-pressed={chosen}
                  onClick={() => onChange(option.value)}
                >
                  {option.label}
                </DropdownItem>
              );
            })}
          </section>
        ))}
      </div>
    </Dropdown>
  );
};

PartKeyPicker.propTypes = {
  /** From getPartKeyOptions: the "follows the song" option and the keys in two labelled groups */
  options: PropTypes.shape({
    songOption: PropTypes.shape({
      label: PropTypes.string.isRequired,
      hint: PropTypes.string,
    }).isRequired,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
        ).isRequired,
      }),
    ).isRequired,
  }).isRequired,
  /** The key the part declares, or empty when it follows the song */
  value: PropTypes.string,
  /** The key the part is in when it declares none — the song's, or one inherited from a part before */
  inForceKey: PropTypes.string,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  /** Given the chosen key, or an empty string for "Same key" */
  onChange: PropTypes.func.isRequired,
};

export default PartKeyPicker;
